/**
 * NTF Page 3 — Notifications bell (top-bar chrome). The shell mounts this in the
 * global top bar; NTF supplies the content + deep links + the read API.
 *
 * Bell: MingCute bell line (no unread) / fill + a claret count badge (unread).
 * Click → a panel anchored to the bell, closing on row-navigate / Escape /
 * outside-click. The panel is PORTALLED to <body>: the topbar <header> uses
 * backdrop-filter: blur(), which creates a stacking context that would otherwise
 * trap an in-flow panel behind the page content. Fixed-positioned to the button.
 *
 * Spec: 08-ui-spec.md §NTF Page 3.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NotificationLine, NotificationFill } from '@teivor/shared-ui';

import { useUnreadCount } from '../queries';

import { NotificationList } from './NotificationList';

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const { data } = useUnreadCount();
  const unread = data?.count ?? 0;
  const hasUnread = unread > 0;

  const toggle = React.useCallback(() => {
    if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
    setOpen((o) => !o);
  }, []);

  // Close on outside-click + Escape; reposition on scroll/resize while open.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const reposition = () => {
      if (buttonRef.current) setRect(buttonRef.current.getBoundingClientRect());
    };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const label = hasUnread
    ? t('ntf.bell.label_unread', { count: unread })
    : t('ntf.bell.label');

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-[var(--tvl-r2)] text-[var(--tvl-ink-2)] outline-none transition-colors hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)] focus-visible:outline-2 focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-2"
      >
        {hasUnread ? (
          <NotificationFill size={20} color="currentColor" aria-hidden />
        ) : (
          <NotificationLine size={20} color="currentColor" aria-hidden />
        )}
        {hasUnread ? (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-[var(--tvl-r-full)] bg-[var(--tvl-accent)] px-1 text-[10px] font-semibold leading-[16px] tabular-nums text-[var(--tvl-on-accent)] [font-family:var(--tvl-font-mono)]"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label={t('ntf.bell.label')}
              style={{
                position: 'fixed',
                top: rect.bottom + 8,
                right: Math.max(12, window.innerWidth - rect.right),
              }}
              className="z-[var(--tvl-z-popover)] w-96 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)] shadow-[var(--tvl-e2)]"
            >
              <NotificationList variant="panel" onNavigate={() => setOpen(false)} />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
